
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

interface PropertyFinancialInfoProps {
  price: number;
  arvValue: number | null;
  grossMargin: number | null;
  contractExpirationDate: string | null;
  daysOnSite: number;
  formatCurrency: (amount: number | null | undefined) => string;
  formatDate: (dateString: string | null | Date) => string;
  status?: string;
  closedDate?: string | null;
  salePrice?: number | null;
  financingType?: string | null;
  hideGrossMargin?: boolean;
}

export const PropertyFinancialInfo = ({
  price,
  arvValue,
  grossMargin,
  contractExpirationDate,
  daysOnSite,
  formatCurrency,
  formatDate,
  status,
  closedDate,
  salePrice,
  financingType,
  hideGrossMargin = false
}: PropertyFinancialInfoProps) => {
  const isSold = status === 'sold';
  const displayPrice = isSold && salePrice ? salePrice : price;
  const priceLabel = isSold && salePrice ? 'Sale Price' : 'Asking Price';
  const showListedPrice = isSold && salePrice && salePrice !== price;

  return (
    <Card>
      <CardContent className="p-4">
        <h3 className="font-semibold mb-4">Financial Information</h3>
        <div className={`grid ${hideGrossMargin ? 'grid-cols-2' : 'grid-cols-3'} gap-4`}>
          <div>
            <p className="text-sm text-muted-foreground">{priceLabel}</p>
            <p className="font-bold text-lg text-godyspo-gold">{formatCurrency(displayPrice)}</p>
            {showListedPrice && (
              <p className="text-xs text-muted-foreground">Listed: {formatCurrency(price)}</p>
            )}
          </div>
          <div>
            <p className="text-sm text-muted-foreground">ARV</p>
            <p className="font-bold text-lg">{formatCurrency(arvValue)}</p>
          </div>
          {!hideGrossMargin && (
            <div>
              <p className="text-sm text-muted-foreground">Gross Margin</p>
              <p className={`font-bold text-lg ${grossMargin && grossMargin < 0 ? 'text-red-500' : ''}`}>
                {grossMargin !== null ? formatCurrency(grossMargin) : 'N/A'}
              </p>
            </div>
          )}
        </div>
        <Separator className="my-4" />
        <div className="flex justify-between items-center">
          {isSold && closedDate ? (
            <div>
              <p className="text-sm text-muted-foreground">Sold Date</p>
              <p className="font-medium">{formatDate(closedDate)}</p>
            </div>
          ) : (
            <div>
              <p className="text-sm text-muted-foreground">Contract Expires</p>
              <p className="font-medium">{contractExpirationDate ? formatDate(contractExpirationDate) : 'N/A'}</p>
            </div>
          )}
          {isSold && financingType && (
            <div>
              <p className="text-sm text-muted-foreground">Financing Type</p>
              <p className="font-medium">{financingType}</p>
            </div>
          )}
          <div>
            <p className="text-sm text-muted-foreground">Days on Site</p>
            <p className="font-medium">{daysOnSite} days</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
